import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Link,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../contexts/AuthContext";
import CustomButton from "../ui/CustomButton";
import logger from "../../utils/logger";
import { isInAppBrowser } from "../../utils/browserEnv";

const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  minHeight: "100vh",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  paddingTop: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2.25),
  width: "100%",
  maxWidth: 400,
  borderRadius: theme.spacing(4),
  textAlign: "center",
  backgroundColor: theme.palette.background.default,
  boxShadow: "none",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1.5),
  },
}));

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login, googleAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl");
  const redirectTo = location.state?.redirectTo || returnUrl || "/";
  const cartData = location.state?.cartData;

  useEffect(() => {
    // Clean up any stale redirect error from old auth flow
    localStorage.removeItem("loops_google_auth_error");
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        navigate(redirectTo, { state: cartData });
      } else {
        setError(result.message || "Login failed. Please try again.");
      }
    } catch (err) {
      logger.error("❌ Login error:", err);
      setError(err.message || "An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await googleAuth({ redirectTo, cartData });
      if (result.cancelled) {
        setLoading(false);
      } else if (!result.success) {
        setError(result.message || "Google login failed. Please try again.");
        setLoading(false);
      }
      // On success, googleAuth navigates away — no further action needed here.
    } catch (err) {
      logger.error("Google login error:", err);
      setError(err.message || "An error occurred during Google login");
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <BackgroundBox>
      <StyledPaper elevation={0}>
        <Typography
          variant="h5"
          fontWeight="500"
          sx={{ mt: "4rem", mb: "1.5rem" }}
        >
          Welcome Back
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          {isInAppBrowser() && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Google sign-in may not work in this browser (e.g. from Telegram or
              another app).{" "}
              <Link
                href={window.location.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 600 }}
              >
                Open in Safari or Chrome
              </Link>{" "}
              for the best experience.
            </Alert>
          )}

          <CustomButton
            fullWidth
            type="button"
            variant="outlined"
            size="large"
            disabled={loading}
            onClick={handleGoogleLogin}
            sx={{
              height: "50px",
              borderRadius: "25px",
              textTransform: "none",
              backgroundColor: "#fff",
              border: "1px solid #dadce0",
              color: "#3c4043",
              "&:hover": {
                backgroundColor: "#f8f9fa",
                border: "1px solid #dadce0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              },
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              component="img"
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              sx={{
                width: 20,
                height: 20,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
              Continue with Google
            </Typography>
          </CustomButton>

          <Box sx={{ display: "flex", alignItems: "center", my: 3 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" sx={{ px: 2, color: "text.secondary" }}>
              OR
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <StyledTextField
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            placeholder="Email"
            disabled={loading}
          />

          <StyledTextField
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            placeholder="Password"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    aria-label="toggle password visibility"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <CustomButton
            fullWidth
            variant="contained"
            type="submit"
            size="large"
            disabled={loading}
            preventSpam={true}
            spamDelay={2000}
            sx={{
              height: "50px",
              borderRadius: "25px",
              textTransform: "none",
              mt: 2,
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Sign In"
            )}
          </CustomButton>

          <Box mt={3} mb={3}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                ...(loading && {
                  opacity: 0.6,
                  pointerEvents: "none",
                }),
              }}
            >
              Don&apos;t have an account?{" "}
              <Box
                component="span"
                sx={{
                  color: "black",
                  cursor: "pointer",
                }}
                onClick={() =>
                  !loading &&
                  navigate(
                    `/signup?returnUrl=${encodeURIComponent(redirectTo)}`,
                  )
                }
              >
                Sign up
              </Box>
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body2"
              sx={{
                color: "black",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
                ...(loading && {
                  opacity: 0.6,
                  pointerEvents: "none",
                }),
              }}
              onClick={() => !loading && navigate("/forgot-password")}
            >
              Forgot password?
            </Typography>
          </Box>
        </form>
      </StyledPaper>
    </BackgroundBox>
  );
};

export default Login;
